import { FIELD_LABEL_CLASS } from '../fieldLabel.js';
import { DateTimePicker } from './DateTimePicker.js';
import {
  type AllowedWindow,
  type OccupiedWindow,
  latestReturnBefore,
  rangeOverlapsAny,
} from './useDateTimePicker.js';

// Minimum rental duration; also the gap between pickup and the earliest return.
const MIN_DURATION_MS = 30 * 60 * 1000;

export interface DateRange {
  pickup: Date | null;
  return: Date | null;
}

export interface DateRangePickerProps {
  pickup: Date | null;
  return: Date | null;
  onChange: (range: DateRange) => void;
  minDate?: Date;
  maxDate?: Date;
  allowedWindow?: AllowedWindow | null;
  /** 205 (#292): same list passed to BOTH the pickup and return pickers — one car, one fetch. */
  occupiedWindows?: OccupiedWindow[];
  disabled?: boolean;
  error?: string;
}

// 169: the type treatment moved to the shared constant — the home page's own field label had
// drifted away from it. Only the spacing stays local.
const labelClass = `${FIELD_LABEL_CLASS} mb-1.5`;

// Two composed DateTimePickers with range rules: return is always ≥ pickup + 30min,
// and moving pickup past an existing return drops the now-invalid return.
export function DateRangePicker({
  pickup,
  return: returnValue,
  onChange,
  minDate,
  maxDate,
  allowedWindow,
  occupiedWindows,
  disabled,
  error,
}: DateRangePickerProps) {
  // Earliest allowed return: 30 min after pickup (falls back to the shared min).
  const returnMinDate = pickup ? new Date(pickup.getTime() + MIN_DURATION_MS) : minDate;
  // 386 (FR-018): no return is possible after the next rental or block starts, so the calendar greys those days.
  const blockedFrom = pickup ? latestReturnBefore(occupiedWindows ?? [], pickup) : null;
  const returnMaxDate =
    blockedFrom && (!maxDate || blockedFrom.getTime() < maxDate.getTime()) ? blockedFrom : maxDate;

  const handlePickupChange = (next: Date) => {
    const earliestReturn = new Date(next.getTime() + MIN_DURATION_MS);
    // 386 (#1301): a kept return must also leave the whole rental clear of other rentals.
    const keepReturn =
      returnValue &&
      returnValue >= earliestReturn &&
      !rangeOverlapsAny(occupiedWindows ?? [], next, returnValue)
        ? returnValue
        : null;
    onChange({ pickup: next, return: keepReturn });
  };

  const handleReturnChange = (next: Date) => {
    onChange({ pickup, return: next });
  };

  return (
    <div>
      <div className="mb-4">
        <span className={labelClass}>Pickup date &amp; time</span>
        <DateTimePicker
          value={pickup}
          onChange={handlePickupChange}
          minDate={minDate}
          maxDate={maxDate}
          allowedWindow={allowedWindow}
          occupiedWindows={occupiedWindows}
          disabled={disabled}
          placeholder="Select pickup date & time"
        />
      </div>
      <div>
        <span className={labelClass}>Return date &amp; time</span>
        <DateTimePicker
          value={returnValue}
          onChange={handleReturnChange}
          minDate={returnMinDate}
          maxDate={returnMaxDate}
          allowedWindow={allowedWindow}
          occupiedWindows={occupiedWindows}
          rangeStart={pickup ?? undefined}
          disabled={disabled}
          placeholder="Select return date & time"
          error={Boolean(error)}
        />
      </div>
      {error && <p className="mt-1.5 text-sm text-error">{error}</p>}
    </div>
  );
}
